'use client'

import type { ThankYouPageContent } from '../lib/content/types'
import { useInstallerUrl } from '../desktop-link'
import { installerUrl } from '../lib/links'
import type { DesktopOs } from '../lib/links'
import { useDetectedOs, useRequestedOsFromUrl } from '../lib/use-detected-os'
import { Link } from '../navigation'
import { DesktopInstallConversion } from './desktop-install-conversion'
import { InstallTrigger } from './install-trigger'
import { SectionContainer } from './primitives'

/** Human-readable OS names. Proper nouns, intentionally not translated. */
const OS_LABEL: Record<DesktopOs, string> = {
  mac: 'macOS',
  windows: 'Windows',
}

/** Accent palette for the numbered step tiles, matching the mockup. */
const STEP_TILES = [
  'bg-sd-raised text-sd-accent',
  'bg-sd-raised text-sd-accent',
  'bg-sd-raised text-sd-accent',
]

const FALLBACK_VERSION = '1.0'

function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-[46px]"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

/**
 * Confirmation page shown after a download is requested. Honors an explicit
 * `?os=` request from the download CTA so the OS a visitor clicked stays the
 * one that downloads — falling back to client-side OS detection when the
 * page is reached without that query param (e.g. the header's generic link).
 */
export function ThankYouContent({
  content,
  latestVersion,
  requestedOs,
}: {
  content: ThankYouPageContent
  latestVersion?: string | null
  requestedOs?: DesktopOs
}) {
  const detectedOs = useDetectedOs()
  const urlOs = useRequestedOsFromUrl()
  const os = requestedOs ?? urlOs ?? detectedOs
  const other: DesktopOs = os === 'mac' ? 'windows' : 'mac'
  const osLabel = OS_LABEL[os]
  const sub = content.sub.replaceAll('{os}', osLabel)
  const otherLabel = content.other.replaceAll('{other}', OS_LABEL[other])
  const versionLabel = content.version.replaceAll(
    '{version}',
    latestVersion?.trim() || FALLBACK_VERSION,
  )
  const installerHref = useInstallerUrl(installerUrl(os))
  const otherInstallerHref = useInstallerUrl(installerUrl(other))
  const { step01: firstStep, step02, step03 } = content.steps
  const firstStepBody = os === 'mac' ? firstStep.mac : firstStep.win

  const steps = [{ title: firstStep.title, body: firstStepBody }, step02, step03]

  return (
    <section className="bg-theme_semantic_surface_primary-background-elevated px-5 py-16 md:px-10">
      <DesktopInstallConversion />
      <SectionContainer className="max-w-[920px]">
        <Link
          href={{ pathname: '/' }}
          className="inline-flex items-center gap-1.5 text-[14px] font-medium text-sd-slate-2 transition-colors hover:text-sd-ink"
        >
          {content.back}
        </Link>

        <div className="mt-8 flex flex-col items-center text-center">
          <div className="relative animate-saiDrop">
            <div className="flex size-24 items-center justify-center rounded-[28px] bg-studio-gray-0 text-studio-gray-1000">
              <CheckGlyph />
            </div>
          </div>

          <h1 className="mt-8 text-[44px] font-light leading-[1.02] tracking-[-0.035em] text-sd-ink sm:text-[64px]">
            {content.title1}
            <br />
            <span className="font-bold">{content.title2}</span>
          </h1>
          <p className="mt-5 max-w-[52ch] text-[18px] leading-[1.55] text-sd-slate">{sub}</p>

          <div className="mt-[34px] flex flex-wrap items-center justify-center gap-[14px]">
            <InstallTrigger href={installerHref} retryLabel={content.retry} />
            <a
              href={otherInstallerHref}
              className="inline-flex items-center gap-[9px] rounded-[14px] border border-sd-border bg-sd-surface px-[22px] py-[15px] text-[15px] font-semibold text-sd-ink transition-colors hover:bg-sd-raised"
            >
              {otherLabel}
            </a>
          </div>

          <p className="mt-3.5 font-mono text-[12px] font-medium text-sd-slate-3">{versionLabel}</p>
        </div>

        <div className="mt-[60px]">
          <div className="mb-[22px] text-center font-mono text-[13px] font-semibold uppercase tracking-[0.1em] text-sd-accent">
            {content.stepsHeading}
          </div>
          <ol className="grid grid-cols-1 gap-[18px] text-left sm:grid-cols-3">
            {steps.map((step, index) => (
              <li
                key={step.title}
                className="rounded-[20px] border border-sd-border bg-sd-surface p-[26px]"
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className={`flex size-[30px] shrink-0 items-center justify-center rounded-[9px] font-mono text-[14px] font-extrabold ${STEP_TILES[index] ?? STEP_TILES[0]}`}
                  >
                    {index + 1}
                  </span>
                  <span className="text-[16px] font-bold text-sd-ink">{step.title}</span>
                </div>
                <p className="mt-3 text-[14px] leading-[1.55] text-sd-slate">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </SectionContainer>
    </section>
  )
}
